import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export const OBJECT_URL_REVOKE_DELAY_MS = 1000;
const FALLBACK_FILE_NAME = 'file';
const FILENAME_LEAD_IN = 'filename=';

export type FileDownloadEvent =
  | { kind: 'progress'; loadedBytes: number; totalBytes?: number }
  | { kind: 'saved'; fileName: string; sizeBytes: number };

export function sanitizeDownloadFilename(name: string): string {
  return (name || '')
    .replace(/\.\./g, '_')
    .replace(/[\/\\]/g, '_')
    .replace(/[\x00-\x1f\x7f]/g, '_')
    .replace(/[^A-Za-z0-9.\-_ ]/g, '_')
    || FALLBACK_FILE_NAME;
}

export function parseContentDispositionFilename(contentDisposition: string | null): string | null {
  if (!contentDisposition) {
    return null;
  }
  const start = contentDisposition.search(FILENAME_LEAD_IN);
  if (start < 0) {
    return null;
  }
  const value = contentDisposition.substring(start + FILENAME_LEAD_IN.length).split(';')[0].trim();
  if (value.length === 0) {
    return null;
  }
  const firstCharacter = value[0];
  if (firstCharacter !== '"' && firstCharacter !== '\'') {
    return value;
  }
  if (value.length < 2 || value[value.length - 1] !== firstCharacter) {
    return null;
  }
  return value.substring(1, value.length - 1) || null;
}

export function resolveDownloadFilename(headers: HttpHeaders, fallbackFileName: string): string {
  return sanitizeDownloadFilename(parseContentDispositionFilename(headers.get('Content-Disposition')) || fallbackFileName);
}

@Injectable({
  providedIn: 'root',
})
export class FileDownloadService {
  constructor(private readonly http: HttpClient) {}

  download(url: string, fallbackFileName: string): Observable<FileDownloadEvent> {
    return this.http
      .get(url, { responseType: 'blob', observe: 'events', reportProgress: true, withCredentials: true })
      .pipe(
        filter((event: HttpEvent<Blob>) => event.type === HttpEventType.DownloadProgress || event.type === HttpEventType.Response),
        map((event: HttpEvent<Blob>) => {
          if (event.type === HttpEventType.DownloadProgress) {
            return { kind: 'progress', loadedBytes: event.loaded, totalBytes: event.total } as FileDownloadEvent;
          }
          const response = event as HttpResponse<Blob>;
          const fileName = resolveDownloadFilename(response.headers, fallbackFileName);
          this.saveBlob(response.body, fileName);
          return { kind: 'saved', fileName, sizeBytes: response.body ? response.body.size : 0 } as FileDownloadEvent;
        })
      );
  }

  saveBlob(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(url), OBJECT_URL_REVOKE_DELAY_MS);
  }
}
